import _ from "lodash";
import React from "react";
import EventRow from "../EventRow/EventRow";
import { CalendarDateOutlined } from "@toddle-edu/ds-icons";
import {
  mapStateToProps,
  mapActionCreators,
  getIsHolidayEventType,
} from "../../modules/Utils";
import { connect } from "react-redux";
import { useI18n } from "Hooks";
import { DatePicker, Checkbox } from "@toddle-edu/ds-web";
import classNames from "classnames";
import {
  getDateMomentObject,
  getDateMomentObjectCustom,
} from "Utils/DateTimeHelpers";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import {
  getSelectedAcademicYearStartDate,
  getSelectedAcademicYearEndDate,
  getNextOrLastAcademicYearEndDate,
} from "modules/CommonSelectors";
import classes from "./DateTime.scss";
import TimeBasedInput from "./TimeBasedInput";
import { useDateTime } from "../../hooks";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";

import { CURRICULUM_TYPE_DP } from "Constants/stringConstants";
import RREventsFeatureFlagWrapper from "../RREventsFeatureFlagWrapper";
import {
  getSelectedSchoolDetailsForCalendar,
  getSelectedAcademicYearIdForSchool,
  getSelectedAcademicYearForSchool,
} from "../../../../modules/Utils";

const DateTimeContainer = props => {
  const { t } = useI18n(["common", "calendar"]);
  const {
    minDate,
    maxDate,
    onStartDateChange,
    onEndDateChange,
    startDate,
    isAllDayEvent,
    setIsAllDayEvent,
    endDate,
    testId,
    eventUID,
    showBorder,
    dateContainerClass = "",
    isClearable = false,
    setIsRepeatEvent,
    isRepeatEvent,
    fullScreenDateContainer = "",
    showAllDayCheckbox = true,
    repeatLabel,
  } = props;

  const _startDate = _.isEmpty(startDate)
    ? null
    : getDateMomentObject(startDate);
  const _endDate = _.isEmpty(endDate) ? null : getDateMomentObject(endDate);

  const timeBasedContainerClass = classNames({
    [classes.hideDatePicker]: isAllDayEvent,
  });

  const datePickerContainerClass = classNames({
    [classes.datePickerContainer]: true,
    [classes.hideDatePicker]: !isAllDayEvent,
  });

  return (
    <div className={classNames(classes.container, fullScreenDateContainer)}>
      <div className={timeBasedContainerClass}>
        <TimeBasedInput
          {...props}
          t={t}
          startDate={_startDate}
          endDate={_endDate}
          showBorder={showBorder}
          dateContainerClass={dateContainerClass}
        />
      </div>

      <div className={datePickerContainerClass}>
        <div className={classNames(classes.datePicker, dateContainerClass)}>
          <DatePicker
            dsVersion={"2.0"}
            size="medium"
            picker="range"
            variant={showBorder ? "outlined" : "inline"}
            placeholder={[t("common:start_date"), t("common:end_date")]}
            format={DATE_TIME_CONSTANTS.ddd_DD_MMM_YYYY}
            startDate={_startDate}
            endDate={_endDate}
            onStartDateChange={onStartDateChange}
            onEndDateChange={onEndDateChange}
            minDate={minDate}
            maxDate={maxDate}
            testId={`${testId}-start-date`}
            showIcon={false}
          />
        </div>
      </div>

      <div className={classes.fullDayEventContainer}>
        {showAllDayCheckbox ? (
          <Checkbox
            isChecked={isAllDayEvent}
            onChange={setIsAllDayEvent}
            dsVersion={"2.0"}
            testId={`${testId}-fullDayEvent-checkbox`}
            disabled={getIsHolidayEventType(eventUID)}
          >
            <div className={classNames("text-body", classes.label)}>
              {t("calendar:all_day_event")}
            </div>
          </Checkbox>
        ) : null}
        {getIsHolidayEventType(eventUID) ? null : (
          <RREventsFeatureFlagWrapper>
            <Checkbox
              isChecked={isRepeatEvent}
              onChange={setIsRepeatEvent}
              className={showAllDayCheckbox ? classes.repeatEventCheckbox : ""}
              dsVersion={"2.0"}
              label={t("calendar:repeat_event")}
              testId={`${testId}-repeatEvent-checkbox`}
            >
              <div className={classNames("text-body", classes.label)}>
                {repeatLabel ? repeatLabel : t("calendar:repeat_event")}
              </div>
            </Checkbox>
          </RREventsFeatureFlagWrapper>
        )}
      </div>
    </div>
  );
};

const DateTime = props => {
  const {
    setEndDateHandler,
    setStartDateHandler,
    fullDayEventHandler,
    isAllDayEvent,
    selectedAcademicYearStartDate,
    selectedAcademicYearEndDate,
    dateFormat,
    dateTimeContainerClass,
    startDate,
    endDate,
    eventUID,
    repeatEventHandler,
    isRepeatEvent,
  } = useDateTime(props);
  const {
    nextAcademicYearEndDate,
    currentCurriculumProgramType,
    showBorder = false,
    dateContainerClass = "",
    isClearable,
    fullScreenDateContainer = "",
    showAllDayCheckbox = true,
    repeatLabel,
    isSelectedSlotVisible,
  } = props;

  const nextAcademicYearEndDateMomentObject = getDateMomentObjectCustom(
    nextAcademicYearEndDate,
    DATE_TIME_CONSTANTS.YYYY_MM_DD
  );

  const isDpCurriculum = _.isEqual(
    currentCurriculumProgramType,
    CURRICULUM_TYPE_DP
  );

  const maxDateForDatePicker = isDpCurriculum
    ? nextAcademicYearEndDateMomentObject
    : getDateMomentObject(selectedAcademicYearEndDate);

  return (
    <EventRow
      label={"common:date_and_time"}
      labelClass={classes.eventLabel}
      icon={<CalendarDateOutlined variant="subtle" />}
      isRequired={true}
      renderComponent={() => (
        <div
          className={classNames(
            classes.dateTimeContainer,
            dateTimeContainerClass
          )}
        >
          <DateTimeContainer
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDateHandler}
            onEndDateChange={setEndDateHandler}
            isAllDayEvent={isAllDayEvent}
            minDate={getDateMomentObject(selectedAcademicYearStartDate)}
            maxDate={maxDateForDatePicker}
            format={dateFormat}
            setIsAllDayEvent={fullDayEventHandler}
            testId="event-modal-v2-date-time"
            eventUID={eventUID}
            showBorder={showBorder}
            dateContainerClass={dateContainerClass}
            isClearable={isClearable}
            setIsRepeatEvent={repeatEventHandler}
            isRepeatEvent={isRepeatEvent}
            fullScreenDateContainer={fullScreenDateContainer}
            showAllDayCheckbox={showAllDayCheckbox}
            repeatLabel={repeatLabel}
            isSelectedSlotVisible={isSelectedSlotVisible}
          />
        </div>
      )}
    />
  );
};

const localMapStateToProps = state => {
  const selectedAcademicYear = getSelectedAcademicYearForSchool(state);
  const selectedAcademicYearStartDate = getSelectedAcademicYearStartDate({
    selectedAcademicYear,
  });
  const selectedAcademicYearEndDate = getSelectedAcademicYearEndDate({
    selectedAcademicYear,
  });
  const userInfo = getUserInfo();
  const orgId = _.get(userInfo, "org_id");
  const {
    currentCurriculumProgramType,
    currentCurriculumProgramId: curriculumProgramId,
  } = getSelectedSchoolDetailsForCalendar(state);
  const currentAcademicYearId = getSelectedAcademicYearIdForSchool(state);
  return {
    ...mapStateToProps(state),
    selectedAcademicYearStartDate,
    selectedAcademicYearEndDate,
    currentCurriculumProgramType,
    nextAcademicYearEndDate: getNextOrLastAcademicYearEndDate({
      organizationId: orgId,
    }),
    curriculumProgramId,
    currentAcademicYearId,
  };
};

export default connect(localMapStateToProps, mapActionCreators)(DateTime);
